/**
 * JINSA – Pago con CoDi (modal blindado).
 *
 * Flujo:
 *   1. El carrito muestra un solo botón "Pagar con CoDi" (codi-payment-block.liquid).
 *   2. Al abrirlo:
 *      - sin sesión      -> estado "login" (no se confirma nada de forma anónima);
 *      - con sesión y
 *        carrito vacío   -> estado "empty" (nada que pagar, invita a agregar productos);
 *      - con sesión y
 *        carrito con
 *        productos       -> estado "pay" con el total real traído de /cart.js.
 *   3. El botón "Ya pagué con CoDi" arranca DESHABILITADO. Solo se activa con
 *      teléfono válido + checkbox de confirmación marcado. Esa es la barrera
 *      contra el clic impulsivo de quien todavía no depositó.
 *   4. Al confirmar se registra el pedido en el dashboard y SOLO si responde
 *      success === true se: (a) vacía el carrito, (b) pasa al estado "done" con
 *      el número de pedido (#D...). WhatsApp queda como aviso secundario, no
 *      como fuente de verdad: el pedido ya quedó registrado aunque el cliente
 *      nunca mande el mensaje.
 *
 *      Vaciar el carrito al confirmar es lo que permite un pedido nuevo sin
 *      ningún botón de "reset": un carrito vacío nunca se puede volver a pagar
 *      por accidente (cae directo al estado "empty"), y en cuanto el cliente
 *      agrega productos otra vez el modal ya está listo para una compra distinta.
 *      No hace falta ninguna deduplicación por sesión/carrito: el propio estado
 *      del carrito de Shopify es la fuente de verdad.
 *
 * Los totales se calculan aquí (no en Liquid) porque el modal es global y el
 * carrito cambia por AJAX sin recargar la página. Las constantes deben coincidir
 * con snippets/cart-sidebar-footer.liquid.
 */
(function () {
  'use strict';

  var TAX_RATE = 0.16;
  var FREE_SHIPPING_THRESHOLD = 500000; // $5,000.00 en centavos
  var FLAT_SHIPPING = 25000; // $250.00 en centavos

  var modal = null;
  var cartSnapshot = null;
  var lastFocused = null;
  var closeTimer = null;
  // Si vaciamos el carrito pero no hay <cart-sidebar> en la página (ej. estamos
  // parados en /cart, que no tiene el scaffold de AJAX del carrito lateral), no
  // hay forma de refrescar esa tabla en vivo. Se recarga la página al cerrar el
  // modal (no antes, para no interrumpir la lectura del número de pedido).
  var needsPageReloadOnClose = false;

  // ── utilidades ────────────────────────────────────────────────────────────
  function money(cents) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format((cents || 0) / 100);
  }

  function computeTotals(subtotal) {
    var tax = Math.round(subtotal * TAX_RATE);
    var shipping = subtotal > FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING;
    return {
      subtotal: subtotal,
      tax: tax,
      shipping: shipping,
      total: subtotal + tax + shipping,
    };
  }

  function normalizePhone(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function isValidPhone(value) {
    var digits = normalizePhone(value);
    // 10 dígitos nacionales; se permiten más por si escriben lada 52 / +521.
    return digits.length >= 10 && digits.length <= 13;
  }

  function $(selector) {
    return modal ? modal.querySelector(selector) : null;
  }

  function showState(name) {
    var states = modal.querySelectorAll('[data-codi-state]');
    for (var i = 0; i < states.length; i++) {
      states[i].hidden = states[i].getAttribute('data-codi-state') !== name;
    }
  }

  function setError(message) {
    var el = $('[data-codi-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function setBusy(btn, busy, label) {
    var labelEl = btn.querySelector('[data-codi-submit-label]');
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      if (labelEl) {
        if (!labelEl.dataset.originalText) {
          labelEl.dataset.originalText = labelEl.textContent;
        }
        labelEl.textContent = label;
      }
    } else {
      btn.removeAttribute('aria-busy');
      if (labelEl && labelEl.dataset.originalText) {
        labelEl.textContent = labelEl.dataset.originalText;
      }
    }
  }

  // ── vaciar el carrito tras un pedido confirmado ───────────────────────────
  async function clearCart() {
    var sidebar = document.querySelector('cart-sidebar');
    var body = {};
    if (sidebar && typeof sidebar.getSectionsToRender === 'function') {
      body.sections = sidebar.getSectionsToRender().map(function (s) {
        return s.section;
      });
    }

    var clearUrl = (window.routes && window.routes.cart_clear_url) || '/cart/clear.js';
    var res = await fetch(clearUrl, Object.assign({}, fetchConfig(), { body: JSON.stringify(body) }));
    var data = await res.json();

    if (sidebar && data.sections && typeof sidebar.renderContents === 'function') {
      // Refresca ícono del carrito, lista y footer sin recargar la página, para
      // no interrumpir el modal que sigue abierto mostrando el número de pedido.
      sidebar.renderContents(data);
    } else {
      needsPageReloadOnClose = true;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  function renderTotals(totals) {
    var totalText = money(totals.total);
    $('[data-codi-total]').textContent = totalText;
    $('[data-codi-total-inline]').textContent = totalText;
    $('[data-codi-subtotal]').textContent = money(totals.subtotal);
    $('[data-codi-iva]').textContent = money(totals.tax);
    $('[data-codi-shipping]').textContent =
      totals.shipping === 0 ? 'Gratis' : money(totals.shipping);
  }

  function renderIdentity() {
    var name = modal.getAttribute('data-customer-name') || '';
    var email = modal.getAttribute('data-customer-email') || '';
    $('[data-codi-identity-name]').textContent = name;
    $('[data-codi-identity-email]').textContent = email;
  }

  function buildWhatsappUrl(orderNumber, totals, phone) {
    var number = modal.getAttribute('data-whatsapp');
    var name = modal.getAttribute('data-customer-name') || '';
    var email = modal.getAttribute('data-customer-email') || '';

    var lines = [
      '🟢 *PAGO CoDi REALIZADO* 🟢',
      '',
      'Pedido: *' + (orderNumber || 'sin número') + '*',
      'Cliente: ' + name,
      'Correo: ' + email,
      'Teléfono: ' + phone,
      'Total pagado: ' + money(totals.total),
      '',
      '📎 Adjunto la foto de mi comprobante en este chat.',
    ];

    return 'https://wa.me/' + number + '?text=' + encodeURIComponent(lines.join('\n'));
  }

  function showDone(orderNumber, totals, phone) {
    showState('done');
    $('[data-codi-order-number]').textContent = orderNumber || 'Registrado';
    $('[data-codi-wa]').href = buildWhatsappUrl(orderNumber, totals, phone);
  }

  // ── validación en vivo del formulario ─────────────────────────────────────
  function refreshSubmitState() {
    var phoneInput = $('[data-codi-phone]');
    var agree = $('[data-codi-agree]');
    var submit = $('[data-codi-submit]');
    if (!phoneInput || !agree || !submit) return;

    var ready = isValidPhone(phoneInput.value) && agree.checked && !!cartSnapshot;
    submit.disabled = !ready;
  }

  // ── apertura / cierre ─────────────────────────────────────────────────────
  async function loadCart() {
    var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('No se pudo leer el carrito.');
    return res.json();
  }

  async function openModal() {
    if (!modal) return;

    // Si el modal se cerró hace menos de lo que dura la animación, el timeout
    // de cierre todavía está pendiente y ocultaría este que acabamos de abrir.
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    lastFocused = document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    // deja que el navegador pinte el estado inicial antes de animar
    requestAnimationFrame(function () {
      modal.classList.add('is-active');
    });

    if (modal.getAttribute('data-logged-in') !== 'true') {
      showState('login');
      return;
    }

    try {
      cartSnapshot = await loadCart();
    } catch (e) {
      cartSnapshot = null;
      showState('pay');
      setError('No pudimos leer tu carrito. Recarga la página e inténtalo de nuevo.');
      refreshSubmitState();
      return;
    }

    if (!cartSnapshot.items || !cartSnapshot.items.length) {
      // Carrito vacío: o nunca agregaron nada, o ya pagaron y se vació solo.
      // Cualquiera de los dos casos lleva al mismo siguiente paso.
      cartSnapshot = null;
      showState('empty');
      return;
    }

    showState('pay');
    setError(null);
    renderIdentity();
    resetConfirmFields();

    var totals = computeTotals(cartSnapshot.total_price);
    renderTotals(totals);
    refreshSubmitState();
  }

  // Cada apertura del formulario de pago es, por definición, un pedido nuevo
  // (si fuera el mismo, el carrito ya estaría vacío y habría caído en el
  // estado "empty"). El checkbox de "ya pagué" confirma un monto específico,
  // así que NUNCA debe quedar marcado de una vez anterior — si lo dejáramos
  // así, un pedido nuevo podría confirmarse con un solo clic sin que el
  // cliente vuelva a leer el total. El teléfono sí se conserva: es solo un
  // dato de contacto, no una confirmación de pago.
  function resetConfirmFields() {
    var phoneInput = $('[data-codi-phone]');
    var agree = $('[data-codi-agree]');
    if (phoneInput) {
      phoneInput.classList.remove('is-invalid');
      if (!phoneInput.value) {
        phoneInput.value = modal.getAttribute('data-customer-phone') || '';
      }
    }
    if (agree) agree.checked = false;
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-active');
    document.body.style.overflow = '';

    // El carrito se vació pero no hay <cart-sidebar> para refrescarlo en vivo
    // (ej. estábamos en /cart): se recarga la página al cerrar, no antes, para
    // no cortarle al cliente la lectura del número de pedido.
    if (needsPageReloadOnClose) {
      window.location.reload();
      return;
    }

    closeTimer = window.setTimeout(function () {
      modal.hidden = true;
      closeTimer = null;
    }, 250);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  // ── envío ─────────────────────────────────────────────────────────────────
  async function submitCodi() {
    var submit = $('[data-codi-submit]');
    var phoneInput = $('[data-codi-phone]');
    var phone = normalizePhone(phoneInput.value);

    if (!isValidPhone(phone)) {
      phoneInput.classList.add('is-invalid');
      setError('Revisa tu teléfono: necesitamos 10 dígitos para localizar tu depósito.');
      return;
    }
    phoneInput.classList.remove('is-invalid');
    setError(null);

    // Relee el carrito justo antes de enviar: pudo cambiar en otra pestaña
    // mientras el cliente estaba en la app del banco.
    try {
      cartSnapshot = await loadCart();
    } catch (e) {
      setError('No pudimos confirmar tu carrito. Recarga la página e inténtalo de nuevo.');
      return;
    }

    if (!cartSnapshot.items || !cartSnapshot.items.length) {
      setError('Tu carrito está vacío.');
      return;
    }

    var totals = computeTotals(cartSnapshot.total_price);
    renderTotals(totals);

    // Sin endpoint configurado: no hay registro automático, se cae al aviso
    // manual por WhatsApp para no dejar al cliente sin salida. Ojo: aquí NO se
    // vacía el carrito, porque no tenemos ninguna confirmación real de que el
    // pedido quedó registrado en ningún lado — solo se abrió una ventana de
    // WhatsApp que el cliente pudo cerrar sin enviar nada.
    if (modal.getAttribute('data-auto-order') !== 'true') {
      window.open(buildWhatsappUrl('', totals, phone), '_blank', 'noopener');
      return;
    }

    var customer = { id: null, name: null, email: null, phone: null };
    try {
      customer = JSON.parse(modal.getAttribute('data-customer') || '{}');
    } catch (e) {
      /* deja los valores nulos si el JSON viniera mal formado */
    }
    // El teléfono capturado en el modal manda sobre el de la cuenta: es el que
    // el cliente confirma en el momento del pago.
    customer.phone = phone;

    setBusy(submit, true, 'Registrando pedido…');

    try {
      var payload = {
        source: 'codi_modal',
        cart_token: cartSnapshot.token,
        customer: customer,
        line_items: cartSnapshot.items.map(function (item) {
          return {
            variant_id: item.variant_id,
            quantity: item.quantity,
            title: item.product_title,
            sku: item.sku,
            price: item.price,
          };
        }),
        totals: {
          subtotal: totals.subtotal,
          tax: totals.tax,
          shipping: totals.shipping,
          total: totals.total,
          currency: cartSnapshot.currency,
        },
        payment_method: 'codi',
        payment_status: 'pendiente_verificacion',
      };

      var res = await fetch(modal.getAttribute('data-order-endpoint'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      var data = await res.json();

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'El servidor no pudo registrar el pedido.');
      }

      // El pedido ya quedó registrado en el dashboard: eso es lo que importa.
      // Si vaciar el carrito fallara por lo que sea, no es motivo para mostrarle
      // un error al cliente sobre un pedido que en realidad sí se guardó — se
      // registra en consola para revisarlo, y se sigue mostrando "done".
      try {
        await clearCart();
      } catch (clearErr) {
        console.error('CoDi: el pedido se registró pero no se pudo vaciar el carrito.', clearErr);
      }

      cartSnapshot = null;
      setBusy(submit, false);
      showDone(data.draft_order_number, totals, phone);
    } catch (err) {
      console.error('CoDi: no se pudo registrar el pedido.', err);
      setError(
        'No pudimos registrar tu pedido en este momento. No vuelvas a intentarlo varias veces: ' +
          'escríbenos por WhatsApp con tu comprobante y lo damos de alta manualmente.'
      );
      setBusy(submit, false);
      refreshSubmitState();
    }
  }

  // ── eventos ───────────────────────────────────────────────────────────────
  // Delegado sobre document: el carrito lateral se vuelve a dibujar por AJAX en
  // cada cambio, así que un listener atado directo al botón se perdería.
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-codi-open]')) {
      event.preventDefault();
      openModal();
      return;
    }

    if (!modal) return;

    if (event.target.closest('[data-codi-close]')) {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.target.closest('[data-codi-login]')) {
      event.preventDefault();
      window.location.href = modal.getAttribute('data-login-url');
      return;
    }

    if (event.target.closest('[data-codi-submit]')) {
      event.preventDefault();
      submitCodi();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  function init() {
    modal = document.querySelector('[data-codi-modal]');
    if (!modal) return;

    var phoneInput = $('[data-codi-phone]');
    var agree = $('[data-codi-agree]');

    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        phoneInput.classList.remove('is-invalid');
        refreshSubmitState();
      });
    }
    if (agree) agree.addEventListener('change', refreshSubmitState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();